import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, api, ApiError, apiBaseUrl, Badge, Button, Card, fmtDate, Input, Modal, Select, useFetch, usePagedList } from '@greenrinse/ui';

interface ApiKey { id: string; partnerId: string | null; partnerName: string | null; name: string; keyPrefix: string; scopes: string; createdAtUtc: string; expiresAtUtc: string | null; revokedAtUtc: string | null; lastUsedAtUtc: string | null }
interface Webhook { id: string; partnerId: string | null; partnerName: string | null; url: string; events: string[]; description: string | null; isActive: boolean; consecutiveFailures: number; lastSuccessAtUtc: string | null; lastFailureAtUtc: string | null; createdAtUtc: string }
interface Delivery { id: string; eventId: string; eventType: string; status: 'Pending' | 'Sent' | 'Failed'; attempts: number; responseStatusCode: number | null; lastError: string | null; createdAtUtc: string; sentAtUtc: string | null; nextAttemptAtUtc: string | null }

function SecretReveal({ label, value, hint, onDone }: { label: string; value: string; hint: string; onDone: () => void }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <div className="gr-stack">
      <Alert tone="info">{hint}</Alert>
      <label className="gr-field"><span style={{ fontWeight: 600 }}>{label}</span>
        <div className="gr-row" style={{ flexWrap: 'nowrap' }}>
          <code style={{ flex: 1, padding: '10px 12px', background: 'var(--gr-surface-2)', borderRadius: 10, wordBreak: 'break-all', fontSize: 13 }}>{value}</code>
          <Button variant="outline" style={{ minHeight: 36 }} onClick={() => { void navigator.clipboard.writeText(value).then(() => setCopied(true)); }}>{copied ? '✓' : t('apihooks.copy')}</Button>
        </div>
      </label>
      <div className="gr-row" style={{ justifyContent: 'flex-end' }}><Button onClick={onDone}>{t('common.done')}</Button></div>
    </div>
  );
}

/**
 * API keys + webhook endpoints for one partner. Used by the partner portal (own partner, Partner Admin)
 * and embedded in the admin Partner detail page via `partnerId`.
 */
export default function ApiWebhooksPage({ partnerId, canEdit = true }: { partnerId?: string; canEdit?: boolean }) {
  const { t } = useTranslation();
  const qs = partnerId ? `?partnerId=${partnerId}` : '';
  const keys = useFetch<ApiKey[]>(`/api/api-keys${qs}`);
  const hooks = useFetch<Webhook[]>(`/api/webhooks${qs}`);
  const events = useFetch<string[]>('/api/webhooks/events');
  const [msg, setMsg] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [keyDialog, setKeyDialog] = useState(false);
  const [keyForm, setKeyForm] = useState({ name: '', scopes: 'Read', expiresAtUtc: '' });
  const [secret, setSecret] = useState<{ title: string; value: string; hint: string } | null>(null);
  const [hookDialog, setHookDialog] = useState<Partial<Webhook> | null>(null);
  const [logFor, setLogFor] = useState<Webhook | null>(null);
  const fail = (e: unknown) => setMsg({ tone: 'error', text: e instanceof ApiError ? e.message : t('common.error_generic') });

  const createKey = async () => {
    try {
      const r = await api<{ key: ApiKey; plaintextKey: string }>('/api/api-keys', { method: 'POST', body: { name: keyForm.name, scopes: keyForm.scopes, partnerId: partnerId || null, expiresAtUtc: keyForm.expiresAtUtc || null } });
      setKeyDialog(false); keys.reload();
      setSecret({ title: t('apihooks.your_key'), value: r.plaintextKey, hint: t('apihooks.key_once') });
    } catch (e) { fail(e); }
  };
  const revokeKey = async (k: ApiKey) => { if (!window.confirm(t('apihooks.revoke_confirm', { name: k.name }))) return; await api(`/api/api-keys/${k.id}/revoke`, { method: 'POST' }); keys.reload(); };
  const saveHook = async () => {
    if (!hookDialog?.url) return;
    const body = { url: hookDialog.url, events: hookDialog.events?.length ? hookDialog.events : ['*'], description: hookDialog.description || null, isActive: hookDialog.isActive ?? true, partnerId: partnerId || null };
    try {
      if (hookDialog.id) { await api(`/api/webhooks/${hookDialog.id}`, { method: 'PUT', body }); setHookDialog(null); }
      else {
        const r = await api<{ subscription: Webhook; secret: string }>('/api/webhooks', { method: 'POST', body });
        setHookDialog(null); setSecret({ title: t('apihooks.signing_secret'), value: r.secret, hint: t('apihooks.secret_once') });
      }
      hooks.reload();
    } catch (e) { fail(e); }
  };
  const rotate = async (h: Webhook) => {
    if (!window.confirm(t('apihooks.rotate_confirm'))) return;
    try { const r = await api<{ secret: string }>(`/api/webhooks/${h.id}/rotate-secret`, { method: 'POST' }); setSecret({ title: t('apihooks.signing_secret'), value: r.secret, hint: t('apihooks.secret_once') }); }
    catch (e) { fail(e); }
  };
  const ping = async (h: Webhook) => { try { await api(`/api/webhooks/${h.id}/test`, { method: 'POST' }); setMsg({ tone: 'success', text: t('apihooks.ping_sent') }); } catch (e) { fail(e); } };
  const removeHook = async (h: Webhook) => { if (!window.confirm(t('common.confirm_delete'))) return; await api(`/api/webhooks/${h.id}`, { method: 'DELETE' }); hooks.reload(); };
  const toggleEvent = (ev: string) => {
    if (!hookDialog) return;
    const cur = hookDialog.events ?? ['*'];
    const next = ev === '*' ? ['*'] : cur.includes(ev) ? cur.filter((x) => x !== ev) : [...cur.filter((x) => x !== '*'), ev];
    setHookDialog({ ...hookDialog, events: next.length ? next : ['*'] });
  };

  return (
    <div className="gr-stack">
      {msg && <Alert tone={msg.tone}>{msg.text}</Alert>}

      <Card small style={{ padding: 0 }}>
        <div className="gr-row" style={{ padding: '10px 14px' }}>
          <div><strong>🔑 {t('apihooks.keys')}</strong><div className="gr-muted" style={{ fontSize: 13 }}>{t('apihooks.keys_hint', { url: `${apiBaseUrl || window.location.origin}/api/v1` })}</div></div>
          <span className="gr-topbar__spacer" />
          {canEdit && <Button style={{ minHeight: 34 }} onClick={() => { setKeyForm({ name: '', scopes: 'Read', expiresAtUtc: '' }); setKeyDialog(true); }}>+ {t('apihooks.new_key')}</Button>}
        </div>
        <table className="gr-table">
          <thead><tr><th>{t('apihooks.name')}</th><th>{t('apihooks.key')}</th><th>{t('apihooks.scopes')}</th><th>{t('apihooks.last_used')}</th><th></th></tr></thead>
          <tbody>
            {(keys.data ?? []).map((k) => (
              <tr key={k.id} style={{ opacity: k.revokedAtUtc ? 0.5 : 1 }}>
                <td><strong>{k.name}</strong><div className="gr-muted" style={{ fontSize: 12 }}>{fmtDate(k.createdAtUtc, { dateStyle: 'medium' })}{k.expiresAtUtc ? ` · ${t('apihooks.expires')} ${fmtDate(k.expiresAtUtc, { dateStyle: 'medium' })}` : ''}</div></td>
                <td><code>grk_{k.keyPrefix}_…</code>{k.revokedAtUtc && <Badge tone="error">{t('apihooks.revoked')}</Badge>}</td>
                <td>{k.scopes}</td>
                <td>{k.lastUsedAtUtc ? fmtDate(k.lastUsedAtUtc) : <span className="gr-muted">{t('apihooks.never')}</span>}</td>
                <td style={{ textAlign: 'end' }}>{canEdit && !k.revokedAtUtc && <Button variant="ghost" style={{ minHeight: 30, padding: '0 8px' }} onClick={() => void revokeKey(k)}>{t('apihooks.revoke')}</Button>}</td>
              </tr>
            ))}
            {keys.data?.length === 0 && <tr><td colSpan={5} className="gr-muted">{t('apihooks.no_keys')}</td></tr>}
          </tbody>
        </table>
      </Card>

      <Card small style={{ padding: 0 }}>
        <div className="gr-row" style={{ padding: '10px 14px' }}>
          <div><strong>🪝 {t('apihooks.webhooks')}</strong><div className="gr-muted" style={{ fontSize: 13 }}>{t('apihooks.webhooks_hint')}</div></div>
          <span className="gr-topbar__spacer" />
          {canEdit && <Button style={{ minHeight: 34 }} onClick={() => setHookDialog({ events: ['*'], isActive: true })}>+ {t('apihooks.new_webhook')}</Button>}
        </div>
        <table className="gr-table">
          <thead><tr><th>URL</th><th>{t('apihooks.events')}</th><th>{t('apihooks.status')}</th><th></th></tr></thead>
          <tbody>
            {(hooks.data ?? []).map((h) => (
              <tr key={h.id}>
                <td><code style={{ fontSize: 13 }}>{h.url}</code>{h.description && <div className="gr-muted" style={{ fontSize: 12 }}>{h.description}</div>}</td>
                <td>{h.events.map((e) => <Badge key={e}>{e}</Badge>)}</td>
                <td>
                  {h.isActive ? <Badge tone="success">{t('apihooks.active')}</Badge> : <Badge tone="error">{t('apihooks.paused')}</Badge>}
                  {h.consecutiveFailures > 0 && <div style={{ fontSize: 12, color: 'var(--gr-warning)' }}>{t('apihooks.failures', { n: h.consecutiveFailures })}</div>}
                  <div className="gr-muted" style={{ fontSize: 12 }}>{h.lastSuccessAtUtc ? `✓ ${fmtDate(h.lastSuccessAtUtc)}` : t('apihooks.never')}</div>
                </td>
                <td style={{ textAlign: 'end', whiteSpace: 'nowrap' }}>
                  <Button variant="ghost" style={{ minHeight: 30, padding: '0 8px' }} onClick={() => { setLogFor(h); }}>{t('apihooks.log')}</Button>
                  {canEdit && <>
                    <Button variant="ghost" style={{ minHeight: 30, padding: '0 8px' }} onClick={() => void ping(h)}>{t('apihooks.ping')}</Button>
                    <Button variant="ghost" style={{ minHeight: 30, padding: '0 8px' }} onClick={() => setHookDialog(h)}>{t('common.edit')}</Button>
                    <Button variant="ghost" style={{ minHeight: 30, padding: '0 8px' }} onClick={() => void rotate(h)}>{t('apihooks.rotate')}</Button>
                    <Button variant="ghost" style={{ minHeight: 30, padding: '0 8px' }} onClick={() => void removeHook(h)}>{t('common.delete')}</Button>
                  </>}
                </td>
              </tr>
            ))}
            {hooks.data?.length === 0 && <tr><td colSpan={4} className="gr-muted">{t('apihooks.no_webhooks')}</td></tr>}
          </tbody>
        </table>
      </Card>

      <p className="gr-muted" style={{ fontSize: 13 }}>{t('apihooks.docs_hint')} <code>docs/partner-api.md</code></p>

      <Modal open={keyDialog} title={t('apihooks.new_key')} onClose={() => setKeyDialog(false)}>
        <div className="gr-stack">
          <Input label={t('apihooks.name')} value={keyForm.name} onChange={(e) => setKeyForm({ ...keyForm, name: e.target.value })} placeholder="e.g. Fleet sync from our ERP" />
          <Select label={t('apihooks.scopes')} value={keyForm.scopes} onChange={(e) => setKeyForm({ ...keyForm, scopes: e.target.value })}
            options={[{ value: 'Read', label: t('apihooks.scope_read') }, { value: 'Read, FleetWrite', label: t('apihooks.scope_read_write') }]} />
          <Input label={t('apihooks.expires_optional')} type="date" value={keyForm.expiresAtUtc} onChange={(e) => setKeyForm({ ...keyForm, expiresAtUtc: e.target.value })} />
          <div className="gr-row" style={{ justifyContent: 'flex-end' }}><Button variant="outline" onClick={() => setKeyDialog(false)}>{t('common.cancel')}</Button><Button disabled={!keyForm.name} onClick={() => void createKey()}>{t('common.save')}</Button></div>
        </div>
      </Modal>

      <Modal open={hookDialog !== null} title={hookDialog?.id ? t('apihooks.edit_webhook') : t('apihooks.new_webhook')} onClose={() => setHookDialog(null)}>
        {hookDialog && (
          <div className="gr-stack">
            <Input label="URL" placeholder="https://…" value={hookDialog.url ?? ''} onChange={(e) => setHookDialog({ ...hookDialog, url: e.target.value })} hint={t('apihooks.url_hint')} />
            <div>
              <span style={{ fontWeight: 600 }}>{t('apihooks.events')}</span>
              <div className="gr-row" style={{ marginTop: 6 }}>
                {['*', ...(events.data ?? [])].map((ev) => {
                  const on = (hookDialog.events ?? ['*']).includes(ev);
                  return <button key={ev} type="button" className={`gr-btn ${on ? 'gr-btn--primary' : 'gr-btn--outline'}`} style={{ minHeight: 30, fontSize: 13 }} onClick={() => toggleEvent(ev)}>{ev === '*' ? t('apihooks.all_events') : ev}</button>;
                })}
              </div>
            </div>
            <Input label={t('apihooks.description')} value={hookDialog.description ?? ''} onChange={(e) => setHookDialog({ ...hookDialog, description: e.target.value })} />
            <label className="gr-row"><input type="checkbox" checked={hookDialog.isActive ?? true} onChange={(e) => setHookDialog({ ...hookDialog, isActive: e.target.checked })} /> {t('apihooks.active')}</label>
            <div className="gr-row" style={{ justifyContent: 'flex-end' }}><Button variant="outline" onClick={() => setHookDialog(null)}>{t('common.cancel')}</Button><Button disabled={!hookDialog.url} onClick={() => void saveHook()}>{t('common.save')}</Button></div>
          </div>
        )}
      </Modal>

      <Modal open={secret !== null} title={secret?.title ?? ''} onClose={() => setSecret(null)}>
        {secret && <SecretReveal label={secret.title} value={secret.value} hint={secret.hint} onDone={() => setSecret(null)} />}
      </Modal>

      <Modal open={logFor !== null} wide title={`${t('apihooks.deliveries')} · ${logFor?.url ?? ''}`} onClose={() => setLogFor(null)}>
        {logFor && <DeliveryLog subscriptionId={logFor.id} />}
      </Modal>
    </div>
  );
}

function DeliveryLog({ subscriptionId }: { subscriptionId: string }) {
  const { t } = useTranslation();
  const log = usePagedList<Delivery>(`/api/webhooks/${subscriptionId}/deliveries`, {}, 25);
  return (
          <table className="gr-table">
            <thead><tr><th>{t('apihooks.event')}</th><th>{t('apihooks.status')}</th><th>{t('apihooks.attempts')}</th><th>{t('apihooks.when')}</th><th>{t('apihooks.result')}</th></tr></thead>
            <tbody>
              {(log.data?.items ?? []).map((d) => (
                <tr key={d.id}>
                  <td><code style={{ fontSize: 12 }}>{d.eventType}</code><div className="gr-muted" style={{ fontSize: 11 }}>{d.eventId}</div></td>
                  <td><Badge tone={d.status === 'Sent' ? 'success' : d.status === 'Failed' ? 'error' : 'warning'}>{d.status}</Badge></td>
                  <td>{d.attempts}{d.nextAttemptAtUtc && <div className="gr-muted" style={{ fontSize: 11 }}>{t('apihooks.retry_at')} {fmtDate(d.nextAttemptAtUtc)}</div>}</td>
                  <td>{fmtDate(d.createdAtUtc)}</td>
                  <td>{d.responseStatusCode ?? '—'}{d.lastError && <div style={{ fontSize: 11, color: 'var(--gr-error)' }}>{d.lastError.slice(0, 120)}</div>}</td>
                </tr>
              ))}
              {log.data?.items.length === 0 && <tr><td colSpan={5} className="gr-muted">{t('apihooks.no_deliveries')}</td></tr>}
            </tbody>
          </table>
  );
}
